/**
 * Unified coach-presence logic shared by the coach chat modal, the dojo box
 * coach section, and the sticky top-of-screen bubble.
 *
 * "Thinking" is a single derived signal so all three surfaces light up together:
 *   - a daily insight is generating (isGeneratingRec), or
 *   - an activity-triggered coach update is mid-flight (activityUpdateInFlight), or
 *   - any completed/expired activity event is still awaiting its coach reply
 *     (generation_state pending or claimed).
 */

const AWAITING_STATES = ['pending', 'claimed'];
const isAwaitingWrittenReply = event => (
  AWAITING_STATES.includes(event?.generation_state) && event?.reply_worthwhile !== false
);

// The recent-event window can omit an older still-active review. The separate
// authoritative awaiting flag keeps one non-event-specific cue visible.
export const shouldShowUnscopedActivityReviewToast = (hasAwaiting, visibleEventIds = []) => (
  Boolean(hasAwaiting) && (!Array.isArray(visibleEventIds) || visibleEventIds.length === 0)
);

let currentCoachThinking = false;
const thinkingListeners = new Set();

export const publishCoachThinking = (thinking) => {
  const next = Boolean(thinking);
  if (next === currentCoachThinking) return;
  currentCoachThinking = next;
  thinkingListeners.forEach(listener => listener(next));
};

export const getCoachThinkingSnapshot = () => currentCoachThinking;

export const subscribeCoachThinking = (listener) => {
  thinkingListeners.add(listener);
  listener(currentCoachThinking);
  return () => thinkingListeners.delete(listener);
};

const askTypingRequestIds = new Set();
const askTypingListeners = new Set();
let coachHubOpen = false;
const coachHubOpenListeners = new Set();

export const publishCoachAskTyping = (requestId, isTyping) => {
  const key = String(requestId || '').trim();
  if (!key) return;
  const before = askTypingRequestIds.size > 0;
  if (isTyping) askTypingRequestIds.add(key);
  else askTypingRequestIds.delete(key);
  const after = askTypingRequestIds.size > 0;
  if (before !== after) askTypingListeners.forEach(listener => listener(after));
};
export const getCoachAskTypingSnapshot = () => askTypingRequestIds.size > 0;
export const subscribeCoachAskTyping = (listener) => {
  askTypingListeners.add(listener); listener(getCoachAskTypingSnapshot());
  return () => askTypingListeners.delete(listener);
};
export const publishCoachHubOpen = (open) => {
  const next = Boolean(open);
  if (next === coachHubOpen) return;
  coachHubOpen = next;
  coachHubOpenListeners.forEach(listener => listener(next));
};
export const getCoachHubOpenSnapshot = () => coachHubOpen;
export const subscribeCoachHubOpen = (listener) => {
  coachHubOpenListeners.add(listener); listener(coachHubOpen);
  return () => coachHubOpenListeners.delete(listener);
};

export const deriveCoachThinking = ({
  isGeneratingRec = false,
  activityUpdateInFlight = false,
  activityEvents = [],
} = {}) => {
  if (isGeneratingRec || activityUpdateInFlight) return true;
  return (Array.isArray(activityEvents) ? activityEvents : [])
    .some(isAwaitingWrittenReply);
};

export const activityReviewPresenceFromTimelineMessage = (current, message = {}) => {
  if (message.state === 'generated' || message.state === 'failed') return false;
  if (message.state === 'reaction') return message.replyWorthwhile !== false;
  if (message.state === 'retrying' || String(message.reason || '').startsWith('activity_')) return true;
  return Boolean(current);
};

// WebSocket delivery is best-effort across reconnects and deploys. The canonical
// activity-event endpoint is authoritative: if it has no pending/claimed work,
// a local `activityUpdateInFlight` flag is stale and must not keep the UI in
// "reviewing…" forever.
export const reconcileActivityReviewPresence = (_current, activityEvents = [], hasAwaitingActivityReview) => (
  typeof hasAwaitingActivityReview === 'boolean'
    ? hasAwaitingActivityReview
    : (Array.isArray(activityEvents) ? activityEvents : [])
      .some(isAwaitingWrittenReply)
);

export const canApplyCanonicalPresenceSnapshot = (signalVersionAtRequest, currentSignalVersion) => (
  Number(signalVersionAtRequest) === Number(currentSignalVersion)
);

export const coachReviewToastKey = eventId => {
  const id = String(eventId || '').trim();
  return id ? `coach-ready:${id}` : null;
};

export const coachReadyNotificationKey = (message = {}) => {
  if (message.type === 'recommendation_ready') {
    const id = message.recommendation?._id || message.recommendation?.id;
    return id ? `coach-ready:${String(id)}` : null;
  }
  if (message.type === 'coach_timeline_updated' && message.state === 'generated') {
    const id = message.eventId || message.insightId;
    return coachReviewToastKey(id);
  }
  return null;
};

export const coachAnswerFromRecommendation = (recommendation = {}, maxLength = 800) => {
  const answer = recommendation?.general_advice
    || recommendation?.data?.general_advice
    || recommendation?.strategy_assessment?.status_summary
    || recommendation?.data?.strategy_assessment?.status_summary
    || '';
  return String(answer).trim().slice(0, maxLength);
};

export const findCoachAnswerInHome = (payload = {}, insightId, maxLength = 800) => {
  if (!insightId) return '';
  const target = String(insightId);
  const candidates = [payload?.today_insight, ...(Array.isArray(payload?.insights) ? payload.insights : [])]
    .filter(Boolean);
  const insight = candidates.find(item => String(item?._id || item?.id || '') === target);
  return coachAnswerFromRecommendation(insight, maxLength);
};

// Global "Coach is typing" signal for the reaction/review notification
// surface. Deliberately separate from the thinking bus above: that one is
// derived and republished by the Activities page from its own local state,
// and letting a second, independently-triggered publisher write to it too
// would race the two sources against each other. This bus is keyed by
// event id so overlapping generations (rare, but possible across devices)
// don't let one's stop signal clear the other's start.
const typingEventIds = new Set();
const typingListeners = new Set();
const typingEventListeners = new Set();

export const getCoachReactionTypingEventIdsSnapshot = () => [...typingEventIds];

export const publishCoachReactionTyping = (eventId, isTyping) => {
  const key = String(eventId || '').trim();
  if (!key) return;
  const before = typingEventIds.size > 0;
  const hadKey = typingEventIds.has(key);
  if (isTyping) typingEventIds.add(key);
  else typingEventIds.delete(key);
  const changed = isTyping ? !hadKey : hadKey;
  const after = typingEventIds.size > 0;
  if (changed) {
    const snapshot = getCoachReactionTypingEventIdsSnapshot();
    typingEventListeners.forEach(listener => listener(snapshot));
  }
  if (before !== after) typingListeners.forEach(listener => listener(after));
};

export const getCoachReactionTypingSnapshot = () => typingEventIds.size > 0;

export const subscribeCoachReactionTyping = (listener) => {
  typingListeners.add(listener);
  listener(typingEventIds.size > 0);
  return () => typingListeners.delete(listener);
};

export const subscribeCoachReactionTypingEvents = listener => {
  typingEventListeners.add(listener);
  listener(getCoachReactionTypingEventIdsSnapshot());
  return () => typingEventListeners.delete(listener);
};

export const reconcileCoachReactionTypingEventIds = validEventIds => {
  const valid = new Set((Array.isArray(validEventIds) ? validEventIds : []).map(String));
  const before = typingEventIds.size > 0;
  let changed = false;
  [...typingEventIds].forEach(eventId => {
    if (!valid.has(eventId)) {
      typingEventIds.delete(eventId);
      changed = true;
    }
  });
  if (!changed) return;
  const snapshot = getCoachReactionTypingEventIdsSnapshot();
  typingEventListeners.forEach(listener => listener(snapshot));
  const after = typingEventIds.size > 0;
  if (before !== after) typingListeners.forEach(listener => listener(after));
};

const reactionToastListeners = new Set();
let currentReactionToast = null;
export const publishCoachReactionToast = payload => {
  if (!payload?.eventId || !payload?.reaction) return;
  currentReactionToast = { ...payload };
  reactionToastListeners.forEach(listener => listener(currentReactionToast));
};
export const clearCoachReactionToast = eventId => {
  if (eventId && String(currentReactionToast?.eventId || '') !== String(eventId)) return;
  currentReactionToast = null;
  reactionToastListeners.forEach(listener => listener(null));
};
export const getCoachReactionToastSnapshot = () => currentReactionToast;
export const subscribeCoachReactionToast = listener => {
  reactionToastListeners.add(listener); listener(currentReactionToast);
  return () => reactionToastListeners.delete(listener);
};

// De-dupes a coach_reaction_received notification against a caller-owned
// set of event ids already notified (e.g. a websocket handler's ref) — a
// reconnect or an out-of-order redelivery must not show the same reaction
// twice.
export const shouldNotifyCoachReaction = (seenEventIds, eventId) => {
  const key = String(eventId || '').trim();
  if (!key || !(seenEventIds instanceof Set) || seenEventIds.has(key)) return false;
  seenEventIds.add(key);
  return true;
};

export const findNewlyGeneratedCoachEvents = (previous = [], next = []) => {
  const previousState = new Map((Array.isArray(previous) ? previous : [])
    .map(event => [String(event?._id || ''), event?.generation_state]));
  return (Array.isArray(next) ? next : []).filter(event => (
    event?.generation_state === 'generated'
    && ['pending', 'claimed'].includes(previousState.get(String(event?._id || '')))
  ));
};

export const findNewlyGeneratedCoachEvent = (previous = [], next = []) => (
  findNewlyGeneratedCoachEvents(previous, next)[0] || null
);
